/**
 * Per-tab, per-item draft storage for the React item editor (LAX-9133).
 *
 * Drafts are written to `localStorage` under a composite key that includes a
 * sessionStorage-backed tab id so two tabs editing the same item never collide
 * with each other.
 *
 * Key format: `tmpItem:<tabId>:<primaryKey | "new">`
 */

const TEMP_ITEM_KEY_PREFIX = 'tmpItem';
const TAB_ID_SESSION_KEY = 'byrd.itemEditor.tabId';
const NEW_ITEM_PRIMARY_KEY = 'new';
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type TempItemPayload = Record<string, unknown> & {
    primaryKey__?: string | null;
    __updatedAt?: number;
};

function generateTabId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Fallback for environments without crypto.randomUUID.
    return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Returns a stable per-tab id, generating it on first use.
 * The tab id is stored in `sessionStorage`, which is naturally scoped per
 * browser tab and survives reloads of that tab.
 *
 * The first time this runs in a given tab (no tab id yet), it also sweeps
 * stale entries from `localStorage` to keep storage bounded.
 */
export function getOrCreateTabId(): string {
    try {
        const existing = sessionStorage.getItem(TAB_ID_SESSION_KEY);
        if (existing) return existing;

        const fresh = generateTabId();
        sessionStorage.setItem(TAB_ID_SESSION_KEY, fresh);
        sweepStaleTempItems();
        return fresh;
    } catch {
        // sessionStorage unavailable (e.g. very restricted privacy mode).
        // Fall back to a non-persistent id; collisions remain possible but
        // behavior won't be worse than today.
        return generateTabId();
    }
}

export function buildTempItemKey(
    tabId: string,
    primaryKey: string | null | undefined,
): string {
    const pk = primaryKey && primaryKey.length > 0 ? primaryKey : NEW_ITEM_PRIMARY_KEY;
    return `${TEMP_ITEM_KEY_PREFIX}:${tabId}:${pk}`;
}

export function readTempItem(key: string): TempItemPayload | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw) as TempItemPayload;
    } catch {
        return null;
    }
}

export function writeTempItem(key: string, payload: TempItemPayload): void {
    try {
        const enriched: TempItemPayload = { ...payload, __updatedAt: Date.now() };
        localStorage.setItem(key, JSON.stringify(enriched));
    } catch {
        // Storage full or unavailable; drafts are best-effort.
    }
}

export function removeTempItem(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // ignore
    }
}

/**
 * Removes stale temp-item entries from `localStorage`:
 *  - the legacy single-key `tmpItem` entry (no tab/pk segments) is always removed
 *  - composite `tmpItem:<tabId>:<pk>` entries older than `maxAgeMs` are removed
 *  - composite entries without a parsable timestamp are removed too
 */
export function sweepStaleTempItems(maxAgeMs: number = DEFAULT_MAX_AGE_MS): void {
    try {
        const now = Date.now();
        const toRemove: string[] = [];

        // Snapshot keys first to avoid issues when localStorage is modified during
        // iteration. Iterating by index (with localStorage.length) is unreliable if
        // entries are added/removed concurrently.
        const keys = Object.keys(localStorage);
        for (const key of keys) {
            if (key === TEMP_ITEM_KEY_PREFIX) {
                toRemove.push(key);
                continue;
            }

            if (!key.startsWith(`${TEMP_ITEM_KEY_PREFIX}:`)) continue;

            const payload = readTempItem(key);
            const updatedAt = payload?.__updatedAt;
            if (typeof updatedAt !== 'number' || now - updatedAt > maxAgeMs) {
                toRemove.push(key);
            }
        }

        toRemove.forEach((key) => removeTempItem(key));
    } catch {
        // ignore
    }
}
